/**
 * Proof taken on the spot (owner, 23 Oct 2026): where the phone is, and a
 * photo from its camera — an ASM's selfie at a meeting, a mitra's photo of the
 * marketing board. Both are taken now, in the form, never picked from earlier.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Check, LocateFixed, MapPinOff, RotateCcw } from 'lucide-react';
import type { GeoPoint } from '@bright/shared';

import { PROOF_ASK, appleBrowser, inAnotherAppsBrowser, readLocation, type LocationMiss } from '@/lib/location';
import { shrinkForUpload } from '@/lib/image';
import { Button } from '@/components/ui';

export type LocationState = 'idle' | 'finding' | 'found' | 'failed';

/**
 * The phone's location, asked for when `active` turns on, and again on
 * `retry` — which the forms call as the photo is taken, so the place sent is
 * where the photo was, not where the form was opened. `clear` drops a reading
 * so it can never go out with the next one. A miss says why (`miss`), and is
 * asked again by itself when the person comes back to the page — from the
 * phone's settings, where they allowed it.
 */
export function useLocationFix(active: boolean) {
  const [where, setWhere] = useState<GeoPoint | null>(null);
  const [state, setState] = useState<LocationState>('idle');
  const [miss, setMiss] = useState<LocationMiss | null>(null);
  const round = useRef(0);
  const find = useCallback(() => {
    const mine = ++round.current;
    setState('finding');
    setMiss(null);
    // Precise and fresh if it can be (a cached place is where the phone was before), else the phone's rough place.
    void readLocation(PROOF_ASK).then((read) => {
      // A newer ask, or a closed form, wins over a late answer.
      if (mine !== round.current) return;
      setWhere(read.point);
      setMiss(read.miss);
      setState(read.point ? 'found' : 'failed');
    });
  }, []);
  useEffect(() => {
    if (active) find();
    else {
      round.current += 1;
      setWhere(null);
      setMiss(null);
      setState('idle');
    }
  }, [active, find]);
  // Back from the phone's settings: ask again, without a tap.
  useEffect(() => {
    if (!active || state !== 'failed') return;
    const back = () => {
      if (document.visibilityState === 'visible') find();
    };
    document.addEventListener('visibilitychange', back);
    return () => document.removeEventListener('visibilitychange', back);
  }, [active, state, find]);
  const clear = useCallback(() => {
    round.current += 1;
    setWhere(null);
    setMiss(null);
    setState('idle');
  }, []);
  return { where, state, miss, retry: find, clear };
}

/** What to do about a miss: one line, the checks in order where there are some, and what comes after. */
export interface MissAdvice {
  lead: string;
  steps: string[];
  then: string | null;
}

/**
 * What to do about a miss, in the words of the phone in hand. A refusal is
 * walked in order — the phone's switch, the browser's own permission, then the
 * site's — since the browser will not say which it was (lib/location.ts).
 */
export function missAdvice(miss: LocationMiss | null): MissAdvice {
  if (miss !== 'unsupported' && inAnotherAppsBrowser()) {
    return { lead: 'This page is open inside another app, which does not share the location.', steps: [], then: 'Open it in Chrome (the ⋮ menu → Open in Chrome) and log it there.' };
  }
  if (miss === 'unsupported') return { lead: 'This browser cannot share a location.', steps: [], then: 'Open the app in Chrome, or Safari on an iPhone.' };
  if (miss === 'not-found') {
    return { lead: 'The phone could not find where you are.', steps: [], then: 'Check that Location is on in the quick settings, step near a door or window, and try again.' };
  }
  const reload = 'Then reload this page — it starts the form again, so do this before filling it in.';
  const apple = appleBrowser();
  if (apple === 'Safari') {
    return {
      lead: 'Location is not reaching this page. Check:',
      steps: [
        'Settings → Privacy (or Privacy & Security) → Location Services is on, and Safari Websites is While Using.',
        'In Safari, the button at the left end of the address bar (aA, or a page icon) → Website Settings → Location → Allow.',
      ],
      then: reload,
    };
  }
  if (apple) {
    return { lead: 'Location is not reaching this page. Check:', steps: [`Settings → Privacy (or Privacy & Security) → Location Services is on, and ${apple} is While Using.`], then: reload };
  }
  return {
    lead: 'Location is not reaching this page. Check, in this order:',
    steps: [
      'Location is on — pull down the quick settings.',
      'Chrome may use it — Settings → Apps → Chrome → Permissions → Location → Allow.',
      'This site may use it — tap the icon beside the web address (a lock, or sliders) → Permissions → Location → Allow.',
    ],
    then: 'Then tap Try again, and choose Allow if asked.',
  };
}

/** The save button's one line about a location that did not come. */
export function missShort(miss: LocationMiss | null): string {
  if (miss !== 'unsupported' && inAnotherAppsBrowser()) return 'Open the page in Chrome';
  if (miss === 'unsupported') return 'Open the app in Chrome';
  if (miss === 'not-found') return 'Location not found yet — try again';
  const apple = appleBrowser();
  return apple ? `Allow location for ${apple}, then reload the page` : 'Turn on Location and allow it';
}

/** One line saying whether the location is in, with a way to ask again. */
export function LocationLine({ fix, required = false }: { fix: ReturnType<typeof useLocationFix>; required?: boolean }) {
  const { where, state, retry } = fix;
  const advice = missAdvice(fix.miss);
  if (state === 'found' && where) {
    return (
      <p className="flex items-center gap-2 text-caption text-ink-2" aria-live="polite">
        <Check className="h-4 w-4 shrink-0 text-success" aria-hidden />
        Location found{where.accuracy ? ` · within about ${where.accuracy} m` : ''}
      </p>
    );
  }
  if (state === 'idle') {
    return (
      <p className="flex items-center gap-2 text-caption text-muted" aria-live="polite">
        <LocateFixed className="h-4 w-4 shrink-0 text-muted" aria-hidden />
        Your location is read as the photo is taken.
      </p>
    );
  }
  if (state === 'finding') {
    return (
      <p className="flex items-center gap-2 text-caption text-muted" aria-live="polite">
        <LocateFixed className="h-4 w-4 shrink-0 text-accent" aria-hidden />
        Finding where you are… indoors this can take half a minute.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line bg-surface-2 px-3 py-2" role="alert">
      <span className="flex min-w-0 items-start gap-2 text-caption text-ink-2">
        <MapPinOff className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden />
        <span className="flex min-w-0 flex-col gap-1">
          <span>
            {advice.lead}
            {required && advice.steps.length === 0 ? ' It is needed to save this.' : ''}
          </span>
          {advice.steps.length > 0 ? (
            <ol className="list-decimal space-y-0.5 pl-4">
              {advice.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          ) : null}
          {advice.then ? <span>{advice.then}</span> : null}
        </span>
      </span>
      <Button type="button" size="sm" variant="secondary" onClick={retry}>
        Try again
      </Button>
    </div>
  );
}

/**
 * A photo from the camera, shrunk before it goes up (which also drops its
 * EXIF), with a preview and a retake. `facing` asks for the front camera for a
 * selfie, the back one for a board.
 */
/**
 * A camera inside the page (owner, 29 Oct 2026: "manish tried to log a meeting … selfie wont go in
 * says low memory"). The phone's own camera app takes the screen, and a phone short of memory
 * closes the browser behind it — the page comes back empty, the selfie and the form gone. Here the
 * camera opens in the page, the picture is taken at a modest size, and the browser never leaves
 * the screen. A phone that will not give the page its camera still has the camera app.
 */
function InPageCamera({ facing, onShot, onCancel, onRefused }: { facing: 'user' | 'environment'; onShot: (file: File) => void; onCancel: () => void; onRefused: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const stop = useCallback(() => {
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  }, []);
  useEffect(() => {
    let alive = true;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 960 } }, audio: false })
      .then((media) => {
        if (!alive) {
          media.getTracks().forEach((track) => track.stop());
          return;
        }
        stream.current = media;
        if (video.current) {
          video.current.srcObject = media;
          void video.current.play().then(() => setReady(true)).catch(() => setReady(true));
        }
      })
      .catch(() => {
        if (alive) onRefused();
      });
    return () => {
      alive = false;
      stop();
    };
  }, [facing, onRefused, stop]);

  function shoot() {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    // A modest picture: a face and the place behind it read well at 1280 on the long side.
    const scale = Math.min(1, 1280 / Math.max(v.videoWidth, v.videoHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(v.videoWidth * scale);
    canvas.height = Math.round(v.videoHeight * scale);
    canvas.getContext('2d')?.drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        stop();
        if (blob) onShot(new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' }));
        else onRefused();
      },
      'image/jpeg',
      0.82,
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="relative overflow-hidden rounded-md border border-line bg-black">
        <video ref={video} playsInline muted className={`mx-auto max-h-72 w-full object-contain ${facing === 'user' ? '-scale-x-100' : ''}`} />
        {ready ? null : <p className="absolute inset-0 grid place-items-center text-caption text-white/80">Opening the camera…</p>}
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            stop();
            onCancel();
          }}
        >
          Cancel
        </Button>
        <Button type="button" size="sm" variant="primary" disabled={!ready} onClick={shoot} className="flex-1">
          <Camera className="h-3.5 w-3.5" aria-hidden />
          Take the photo
        </Button>
      </div>
    </div>
  );
}

const canUsePageCamera = () => typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia) && window.isSecureContext;

export function PhotoCapture({
  label,
  hint,
  facing,
  file,
  onFile,
  takeLabel,
}: {
  label: string;
  hint: string;
  facing: 'user' | 'environment';
  file: File | null;
  onFile: (file: File | null) => void;
  takeLabel: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  // The page's camera refused (no permission, an old browser): the camera app it is, as before.
  const [appOnly, setAppOnly] = useState(false);
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const refused = useCallback(() => {
    setLive(false);
    setAppOnly(true);
  }, []);
  const openApp = () => input.current?.click();

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-surface p-3">
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0">
          <span className="block text-label font-medium text-ink">{label}</span>
          <span className="block text-caption text-muted">{hint}</span>
        </span>
        {file ? <Check className="h-4 w-4 shrink-0 text-success" aria-label="Taken" /> : null}
      </div>
      {preview && !live ? <img src={preview} alt="" className="mx-auto max-h-48 w-auto rounded-md border border-line object-contain" /> : null}
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture={facing}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(event) => {
          const picked = event.target.files?.[0];
          event.target.value = '';
          if (!picked) return;
          setBusy(true);
          void shrinkForUpload(picked)
            .then((small) => onFile(small))
            .catch(() => onFile(picked))
            .finally(() => setBusy(false));
        }}
      />
      {live ? (
        <InPageCamera
          facing={facing}
          onShot={(shot) => {
            setLive(false);
            onFile(shot);
          }}
          onCancel={() => setLive(false)}
          onRefused={refused}
        />
      ) : (
        <>
          <Button
            type="button"
            size="sm"
            variant={file ? 'ghost' : 'secondary'}
            loading={busy}
            onClick={() => (canUsePageCamera() && !appOnly ? setLive(true) : openApp())}
          >
            {file ? <RotateCcw className="h-3.5 w-3.5" aria-hidden /> : <Camera className="h-3.5 w-3.5" aria-hidden />}
            {file ? 'Take it again' : takeLabel}
          </Button>
          {appOnly ? (
            <p className="text-caption text-muted">The page could not open the camera — the phone’s camera app opens instead. Allow the camera for this site to take it here.</p>
          ) : canUsePageCamera() ? (
            <button type="button" className="self-start text-caption text-muted underline-offset-2 hover:underline" onClick={openApp}>
              Use the phone’s camera app instead
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
